import React, { useState, useEffect } from 'react';
import { Panel, Button, Alert } from 'react-bootstrap';
import { toProperCase } from '../../utils/Common';
import { getUser } from '../../utils/Common';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { patchUser, delSharing, getUserForBuilderAdmin } from '../../utils/bedrockAPI';

export default function UserInfoPanel({ 
  user, 
  setUser,
  isMobile,
  shareWithEmail,
  builder,
  history,
  userCaps
}) {
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [createdBy, setCreatedBy] = useState(null);

  useEffect(() => {
    if(user && user.createdBy) {
      getUserForBuilderAdmin(user.createdBy).then(cb => {
        setCreatedBy(cb);
      });
    }
  }, [user]);

  const handleResendActivation = async () => {
    if (isSending) return;
    
    setIsSending(true);
    try {
      await shareWithEmail(user.email, user.userId);
      setIsSent(true);
    } catch (error) {
      console.error('Failed to send activation email:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleEditStart = (field, value) => {
    setEditingField(field);
    setEditValue(value || '');
  };

  const handleEditSave = async (field) => {
    if (!user) return;
    
    try {
      await patchUser(user.userId, {[field]: editValue});
      setUser(user => ({...user, [field]: editValue}));

    } catch (error) {
      console.error('Error updating user info:', error);
      // TODO: Add error handling/notification
    }
    
    setEditingField(null);
    setEditValue('');
  };

  const handleEditCancel = () => {
    setEditingField(null);
    setEditValue('');
  };

  const handleCopySuccess = () => {
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleKeyPress = (e, field) => {
    if (e.key === 'Enter') {
      handleEditSave(field);
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  const handleDeleteUser = async () => {
    if (window.confirm(
      'Are you sure you want to delete this user?\n\n' +
      'This will:\n' +
      '- Remove access to all cameras\n' +
      '- Remove association with any employee groups\n' +
      '- Mark the user\'s account for permanent deletion\n\n' +
      'This action cannot be undone.'
    )) {
      try {
        // Remove all camera access
        const userCapIds = userCaps
          .filter(uc => uc.userId === user.userId)
          .map(uc => uc.userCapId);
        
        for (const userCapId of userCapIds) {
          await delSharing(userCapId);
        }

        // Update user status and remove builder association
        await patchUser(user.userId, {
          builderId: null,
          builderStatus: 'DELETE'
        });

        // Redirect to builder access control page
        history.push(`/accesscontrol/${builder.builderId}`);
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Failed to delete user. Please try again.');
      }
    }
  };

  const isAdmin = getUser().role === 'ADMIN';

  return (
    <>
      <Panel className="mb-4">
        <Panel.Heading style={{ fontSize: isMobile ? '1em' : 'inherit' }}>
          <strong>User Information</strong>
        </Panel.Heading>
        <Panel.Body style={{ fontSize: isMobile ? '0.9em' : 'inherit' }}>
          <div>
            <strong>Email:</strong>{' '}
            <div className="email-field-container">
              <span className="non-editable-field">
                {user.email}
              </span>
              {isAdmin && (
                <CopyToClipboard text={user.email} onCopy={handleCopySuccess}>
                  <Button 
                    bsSize="xs" 
                    className="copy-email-button"
                    title={copySuccess ? "Copied!" : "Copy email"}
                  >
                    <span className={`glyphicon ${copySuccess ? 'glyphicon-ok' : 'glyphicon-copy'}`} />
                  </Button>
                </CopyToClipboard>
              )}
            </div>
          </div>
          <div>
            <strong>First Name:</strong>{' '}
            {editingField === 'firstName' ? (
              <div className="editable-field-container">
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => handleKeyPress(e, 'firstName')}
                  className="editable-field-input"
                  autoFocus
                />
                <Button bsSize="xs" onClick={() => handleEditSave('firstName')} className="editable-field-button">
                  ✓
                </Button>
                <Button bsSize="xs" onClick={handleEditCancel}>
                  ✕
                </Button>
              </div>
            ) : (
              <span 
                onClick={() => handleEditStart('firstName', user.firstName)}
                className="editable-field"
              >
                {toProperCase(user.firstName) || 'Click to add'}
              </span>
            )}
          </div>
          <div>
            <strong>Last Name:</strong>{' '}
            {editingField === 'lastName' ? (
              <div className="editable-field-container">
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => handleKeyPress(e, 'lastName')}
                  className="editable-field-input"
                  autoFocus
                />
                <Button bsSize="xs" onClick={() => handleEditSave('lastName')} className="editable-field-button">
                  ✓
                </Button>
                <Button bsSize="xs" onClick={handleEditCancel}>
                  ✕
                </Button>
              </div>
            ) : (
              <span 
                onClick={() => handleEditStart('lastName', user.lastName)}
                className="editable-field"
              >
                {toProperCase(user.lastName) || 'Click to add'}
              </span>
            )}
          </div>
          <div>
            <strong>Phone:</strong>{' '}
            {editingField === 'phone' ? (
              <div className="editable-field-container">
                <input
                  type="tel"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => handleKeyPress(e, 'phone')}
                  className="editable-field-input"
                  placeholder="(123) 456-7890"
                  autoFocus
                />
                <Button bsSize="xs" onClick={() => handleEditSave('phone')} className="editable-field-button">
                  ✓
                </Button>
                <Button bsSize="xs" onClick={handleEditCancel}>
                  ✕
                </Button>
              </div>
            ) : (
              <span 
                onClick={() => handleEditStart('phone', user.phone)}
                className="editable-field"
              >
                {user.phone || 'Click to add'}
              </span>
            )}
          </div>
          {createdBy && (
            <div>
              <strong>User Created by:</strong>{' '}
              <span className="non-editable-field">
                {createdBy.email}
              </span>
            </div>
          )}
          {user.role === 'SHARE_CREATE' && (
            <div className="activation-button-container">
              <Button
                bsStyle="warning"
                onClick={handleResendActivation}
                disabled={isSending || isSent}
                className="resend-activation-button"
              >
                {isSending ? 'Sending...' : isSent ? 'Activation Email Sent!' : 'Resend Activation Email'}
              </Button>
            </div>
          )}
          <div className="builder-actions-container">
            <Button 
              bsStyle={user.builderId === builder.builderId ? "warning" : "success"}
              bsSize={isMobile ? "xs" : null}
              onClick={async () => {
                if (user.builderId === builder.builderId) {
                  if (window.confirm('Are you sure you want to remove this user from your Employee Group? This will remove their association with the company but will not remove their camera access.\n\nTo remove their camera access, use the "Update Access to All Cameras" selector on this page or click "Delete User Permanently" below.')) {
                    await patchUser(user.userId, { builderId: null });
                    history.push(`/accesscontrol/${builder.builderId}`);
                  }
                } else {
                  await patchUser(user.userId, { builderId: builder.builderId });
                  setUser(prev => ({ ...prev, builderId: builder.builderId }));
                }
              }}
            >
              {user.builderId === builder.builderId ? 'Remove User from Employee Group' : 'Add User to Employee Group'}
            </Button>
            {user.builderId === builder.builderId && (
              <div style={{ marginTop: '10px' }}>
                <Button
                  bsStyle="danger"
                  bsSize={isMobile ? "xs" : null}
                  onClick={handleDeleteUser}
                  className="delete-user-button"
                >
                  ⚠️ Delete User ⚠️
                </Button>
              </div>
            )}
          </div>
        </Panel.Body>
      </Panel>
    </>
  );
} 